const METODO_LABEL: Record<string, string> = {
  efectivo:      'Efectivo',
  tarjeta:       'Tarjeta',
  transferencia: 'Transferencia',
  qr:            'Código QR',
};

export interface TicketData {
  // Datos del restaurante (vienen del backend /configuracion)
  restaurante?: string;
  slogan?: string;
  direccion?: string;
  telefono?: string;
  rfc?: string;
  mensaje_pie?: string;
  logo_base64?: string;
  logo_size?: number | string;
  logo_espacio?: number | string;         // px entre el logo y la primera línea de texto
  logo_espacio_arriba?: number | string;  // px por encima del logo
  abrir_cajon?: boolean;
  precuenta?: boolean;
  ticket_mostrar_iva?: string;
  ticket_mostrar_cambio?: string;
  ticket_mostrar_propina?: string;
  precuenta_titulo?: string;
  precuenta_mensaje?: string;
  precuenta_mostrar_precios?: string;
  ticket_lineas_extra?: string;
  ticket_imagen_extra_base64?: string;
  ticket_imagen_extra_size?: number | string;
  ticket_tamano_fuente?: string;
  // Datos de la venta
  mesa_numero: number | string | null;
  tipo?: string | null;
  cliente_nombre?: string | null;
  mesero: string;
  items: { producto: string; cantidad: number; precio_unitario: number; importe: number; descuento_monto?: number; notas?: string | null; modificadores?: { nombre: string; precio_extra: number }[] | null; promo_nombre?: string | null; promo_emoji?: string | null; es_promo?: boolean; promo_items?: { producto: string; cantidad: number; notas?: string | null; modificadores?: { nombre: string; precio_extra: number }[] | null }[] }[];
  subtotal: number;
  iva: number;
  total: number;
  cargos_extra?: { concepto: string; monto: number }[];
  descuento_tipo?: string | null;
  descuento_valor?: number;
  descuento_monto?: number;
  redondeo?: number;
  metodo: string;
  metodo_2?: string | null;
  monto_metodo_1?: number | null;
  monto_metodo_2?: number | null;
  monto_recibido: number;
  propina: number;
  propina_pct?: number;
  cambio: number;
  fecha: string;
  ticket_ancho?: string;
}

/* Aire entre el logo y la primera línea de texto, configurable en el panel
   (Configuración → Ticket → Logo). Los 6 px de siempre son el valor por
   omisión, así que un ticket sin la clave guardada sale igual que antes. */
/* Rollos soportados y su ancho en milímetros. Es el mismo catálogo que ofrece
   el panel en Configuración → Impresoras; el backend tiene su equivalente en
   puntos y columnas (ROLLOS de impresora.js). */
export const ROLLOS_MM: Record<string, number> = { '58mm': 58, '60mm': 60, '80mm': 80 };

export function anchoRolloMm(ticket: { ticket_ancho?: string }): number {
  return ROLLOS_MM[ticket.ticket_ancho || '80mm'] ?? 80;
}

export const ESPACIO_LOGO_DEFAULT = 6;
export function espacioLogoPx(ticket: { logo_espacio?: number | string }): number {
  const n = parseInt(String(ticket.logo_espacio));
  return Number.isFinite(n) ? Math.min(40, Math.max(0, n)) : ESPACIO_LOGO_DEFAULT;
}

/* Aire por encima del logo. Por omisión 0: el ticket empieza pegado al borde
   como siempre, y quien quiera separarlo lo sube desde el panel. */
export function espacioLogoArribaPx(ticket: { logo_espacio_arriba?: number | string }): number {
  const n = parseInt(String(ticket.logo_espacio_arriba));
  return Number.isFinite(n) ? Math.min(40, Math.max(0, n)) : 0;
}

/* El ticket se arma en dos piezas —estilos y contenido— porque se usa de dos
   maneras: dentro de un documento propio (ventana aparte, previsualización) y
   embebido en la página del panel para imprimirlo. En el segundo caso el CSS
   tiene que ir acotado a un contenedor para no pisar los estilos del panel,
   de ahí que `css` reciba el selector raíz. */
function generarPartes(ticket: TicketData): { css: (raiz: string) => string; cuerpo: string; anchoMm: number; restaurante: string } {
  const fecha = new Date(ticket.fecha).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const restaurante = ticket.restaurante || 'CHARGOY';
  const anchoMm = anchoRolloMm(ticket);

  // Un pedido para llevar no tiene mesa: antes salía "Mesa: null" impreso.
  const etiquetaOrigen = (ticket.mesa_numero === null || ticket.mesa_numero === undefined || ticket.mesa_numero === '')
    ? `<strong>Para llevar:</strong> ${ticket.cliente_nombre || 'Mostrador'}`
    : `<strong>Mesa:</strong> ${ticket.mesa_numero}`;
  /* Descuento de un renglón: se escribe bajo el producto como texto (no con
     color ni tachado: la térmica no imprime rojo ni gris y se perdía). Así el
     cliente ve en qué platillo se le descontó, no solo un total al final. */
  const descuentoItemHtml = (item: TicketData['items'][number]) => {
    const desc = Number(item.descuento_monto || 0);
    if (!(desc > 0)) return { etiqueta: '', importe: `$${item.importe.toFixed(2)}` };
    const label = desc >= item.importe - 0.01
      ? 'CORTESÍA'
      : `DESCUENTO ${Math.round(desc / item.importe * 100)}%`;
    return {
      etiqueta: `<br><span class="desc">${label}: -$${desc.toFixed(2)}</span><br><span class="desc">Antes $${item.importe.toFixed(2)}, queda $${(item.importe - desc).toFixed(2)}</span>`,
      importe: `$${(item.importe - desc).toFixed(2)}`,
    };
  };

  const filasItems = ticket.items.map(item => {
    const dsc = descuentoItemHtml(item);
    if (item.es_promo) {
      const subItemsHtml = (item.promo_items || []).map(sub => {
        const subMods = (sub.modificadores || [])
          .map(m => `<br><small style="color:#555">&nbsp;&nbsp;&nbsp;+ ${sub.cantidad > 1 ? `${sub.cantidad}x ` : ''}${m.nombre}</small>`)
          .join('');
        return `<br><small style="color:#6d28d9">&nbsp;&nbsp;· ${sub.cantidad}x ${sub.producto}${subMods}</small>`;
      }).join('');
      return `
    <tr>
      <td><strong style="color:#7c3aed">${item.producto}</strong>${subItemsHtml}${dsc.etiqueta}</td>
      <td class="center">1</td>
      <td class="right">$${item.precio_unitario.toFixed(2)}</td>
      <td class="right">${dsc.importe}</td>
    </tr>`;
    }
    // El modificador aplica a cada pieza de la línea: con cantidad > 1 se muestra "2x EXTRA" y su total
    const n = item.cantidad > 1 ? `${item.cantidad}x ` : '';
    const modsHtml = (item.modificadores || [])
      .map(m => `<br><small style="color:#555">· ${n}${m.nombre}${Number(m.precio_extra) > 0 ? ` +$${(Number(m.precio_extra) * item.cantidad).toFixed(2)}` : ''}</small>`)
      .join('');
    return `
    <tr>
      <td>${item.producto}${modsHtml}${dsc.etiqueta}</td>
      <td class="center">${item.cantidad}</td>
      <td class="right">$${item.precio_unitario.toFixed(2)}</td>
      <td class="right">${dsc.importe}</td>
    </tr>`;
  }).join('');

  const filaDescuento = (ticket.descuento_monto ?? 0) > 0 ? `
    <div class="row desc">
      <span>Descuento${ticket.descuento_tipo === 'porcentaje' ? ` (${ticket.descuento_valor}%)` : ticket.descuento_tipo === 'item' ? ' en productos' : ''}:</span>
      <span>-$${(ticket.descuento_monto ?? 0).toFixed(2)}</span>
    </div>` : '';

  const filaRedondeo = ticket.redondeo ? `
    <div class="row"><span>Redondeo:</span><span>${ticket.redondeo < 0 ? '-' : '+'}$${Math.abs(ticket.redondeo).toFixed(2)}</span></div>` : '';

  const filaPago = ticket.metodo_2 && ticket.monto_metodo_1 != null && ticket.monto_metodo_2 != null ? `
    <div class="row"><span>${METODO_LABEL[ticket.metodo] || ticket.metodo}:</span><span>$${ticket.monto_metodo_1.toFixed(2)}</span></div>
    <div class="row"><span>${METODO_LABEL[ticket.metodo_2] || ticket.metodo_2}:</span><span>$${ticket.monto_metodo_2.toFixed(2)}</span></div>` : `
    <div class="row"><span>Método:</span><span>${METODO_LABEL[ticket.metodo] || ticket.metodo}</span></div>
    <div class="row"><span>Recibido:</span><span>$${ticket.monto_recibido.toFixed(2)}</span></div>`;

  const filaPropina = (ticket.propina > 0 && ticket.ticket_mostrar_propina !== 'false') ? `
    <div class="row"><span>Propina${ticket.propina_pct ? ` (${ticket.propina_pct}%)` : ''}:</span><span>$${ticket.propina.toFixed(2)}</span></div>` : '';

  const filaCambio = (ticket.cambio > 0 && ticket.ticket_mostrar_cambio !== 'false') ? `
    <div class="row cambio"><span>Cambio:</span><span>$${ticket.cambio.toFixed(2)}</span></div>` : '';

  const filaIva = ticket.ticket_mostrar_iva !== 'false'
    ? `<div class="row"><span>IVA:</span><span>$${ticket.iva.toFixed(2)}</span></div>` : '';

  const precuentaSinPrecios = !!ticket.precuenta && ticket.precuenta_mostrar_precios === 'false';

  const logoPct = Math.min(100, Math.max(20, parseInt(String(ticket.logo_size)) || 70));
  const logoHtml = ticket.logo_base64 ? `<img src="${ticket.logo_base64}" style="width:${logoPct}%;height:auto;margin-top:${espacioLogoArribaPx(ticket)}px;margin-bottom:${espacioLogoPx(ticket)}px;display:block;margin-left:auto;margin-right:auto" alt="logo">` : '';

  const imagenExtraPct = Math.min(100, Math.max(20, parseInt(String(ticket.ticket_imagen_extra_size)) || 70));
  const imagenExtraHtml = ticket.ticket_imagen_extra_base64
    ? `<img src="${ticket.ticket_imagen_extra_base64}" style="width:${imagenExtraPct}%;height:auto;margin:6px 0;display:block;margin-left:auto;margin-right:auto" alt="imagen extra">` : '';

  let lineasExtra: { nombre?: string; texto?: string }[] = [];
  try { lineasExtra = JSON.parse(ticket.ticket_lineas_extra || '[]'); } catch { lineasExtra = []; }
  const lineasExtraHtml = lineasExtra
    .filter(l => l.texto)
    .map(l => `<div class="subtitle">${l.nombre ? `<strong>${l.nombre}:</strong> ` : ''}${l.texto}</div>`)
    .join('');

  const FONT_SCALE: Record<string, number> = { pequena: 0.85, normal: 1, grande: 1.25 };
  const fontScale = FONT_SCALE[ticket.ticket_tamano_fuente || 'normal'] ?? 1;
  const px = (base: number) => Math.round(base * fontScale);

  const css = (raiz: string) => `
    ${raiz} *{margin:0;padding:0;box-sizing:border-box}
    ${raiz}{font-family:'Courier New',monospace;font-size:${px(12)}px;width:${anchoMm}mm;margin:0 auto;padding:8px;color:#000;background:#fff}
    ${raiz} .header{text-align:center;margin-bottom:10px}
    ${raiz} .logo-text{font-size:22px;font-weight:bold;letter-spacing:4px}
    ${raiz} .subtitle{font-size:${px(10)}px;color:#555;margin-top:2px}
    ${raiz} .divider{border-top:1px dashed #000;margin:7px 0}
    ${raiz} .info p{margin:2px 0;font-size:${px(11)}px}
    ${raiz} table{width:100%;border-collapse:collapse}
    ${raiz} thead th{border-bottom:1px solid #000;padding:3px 2px;font-size:${px(10)}px;text-transform:uppercase}
    ${raiz} tbody td{padding:3px 2px;vertical-align:top;font-size:${px(11)}px}
    ${raiz} tbody td small{color:#555;font-size:${px(10)}px}
    ${raiz} .center{text-align:center}
    ${raiz} .right{text-align:right}
    ${raiz} .row{display:flex;justify-content:space-between;margin:2px 0;font-size:${px(11)}px}
    ${raiz} .total-line{border-top:1px solid #000;margin-top:4px;padding-top:4px;font-size:${px(14)}px;font-weight:bold}
    ${raiz} .desc{color:#000;font-weight:bold}
    ${raiz} .cambio{font-weight:bold;color:#16A34A}
    ${raiz} .footer{text-align:center;margin-top:10px;font-size:${px(10)}px;color:#555}
  `;

  const cuerpo = `
  <div class="header">
    ${logoHtml}
    ${ticket.slogan ? `<div class="subtitle">${ticket.slogan}</div>` : ''}
    ${ticket.direccion ? `<div class="subtitle">${ticket.direccion}</div>` : ''}
    ${ticket.telefono ? `<div class="subtitle">Tel: ${ticket.telefono}</div>` : ''}
    ${ticket.rfc ? `<div class="subtitle">RFC: ${ticket.rfc}</div>` : ''}
  </div>
  <div class="divider"></div>
  <div class="info">
    <p>${etiquetaOrigen} &nbsp;&nbsp; <strong>Mesero:</strong> ${ticket.mesero}</p>
    <p><strong>Fecha:</strong> ${fecha}</p>
  </div>
  <div class="divider"></div>
  <table>
    <thead><tr>
      <th>Producto</th><th class="center">Cant</th>
      ${precuentaSinPrecios ? '' : '<th class="right">Precio</th><th class="right">Importe</th>'}
    </tr></thead>
    <tbody>${precuentaSinPrecios ? ticket.items.map(item => `
    <tr><td>${item.producto}${(item.modificadores || []).map(m => `<br><small style="color:#555">· ${item.cantidad > 1 ? `${item.cantidad}x ` : ''}${m.nombre}</small>`).join('')}</td><td class="center">${item.cantidad}</td></tr>`).join('') : filasItems}</tbody>
  </table>
  <div class="divider"></div>
  ${precuentaSinPrecios ? '' : `
  <div class="totales">
    <div class="row"><span>Subtotal:</span><span>$${ticket.subtotal.toFixed(2)}</span></div>
    ${filaIva}
    ${(ticket.cargos_extra || []).map(c => `<div class="row"><span>${c.concepto}:</span><span>+$${Number(c.monto).toFixed(2)}</span></div>`).join('')}
    ${filaDescuento}
    ${filaRedondeo}
    <div class="row total-line"><span>TOTAL:</span><span>$${ticket.total.toFixed(2)}</span></div>
  </div>
  <div class="divider"></div>`}
  ${ticket.precuenta ? `
  <div style="text-align:center;margin:4px 0">
    <div style="font-weight:bold;font-size:12px">${ticket.precuenta_titulo || 'PRE-CUENTA'}</div>
    <div style="font-size:11px;color:#555;margin-top:2px">${ticket.precuenta_mensaje || 'ESTE NO ES UN COMPROBANTE FISCAL'}</div>
  </div>` : `
  <div class="pago">
    ${filaPago}
    ${filaPropina}
    ${filaCambio}
  </div>`}
  <div class="divider"></div>
  <div class="footer">
    ${lineasExtraHtml}
    ${imagenExtraHtml}
    <p>${ticket.mensaje_pie || '¡Gracias por su visita!'}</p>
  </div>`;

  return { css, cuerpo, anchoMm, restaurante };
}

/** Documento HTML completo del ticket (ventana aparte y previsualización). */
function generarHTML(ticket: TicketData, autoImprimir = false): string {
  const { css, cuerpo, anchoMm, restaurante } = generarPartes(ticket);
  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Ticket ${restaurante}</title>
  <style>
    ${css('body')}
    @media print{body{margin:0}@page{margin:3mm;size:${anchoMm}mm auto}}
  </style>
</head>
<body>${cuerpo}${autoImprimir
  ? `<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>`
  : ''}
</body>
</html>`;
}

export type ImpresionResultado = 'ok' | 'error' | 'cancelado';

/* Mismo mecanismo que "Imprimir propinas" del corte de caja (Corte.tsx), que
   es el que funciona en las cajas del restaurante:

     - iframe fuera de pantalla, escrito con document.write()
     - el propio documento se manda a imprimir en su window.onload
     - además se llama a print() desde fuera tras un margen de 300 ms
     - el iframe se retira 3 s después, sin depender de 'afterprint'

   Se probaron variantes (esperar 'afterprint' para limpiar, srcdoc, imprimir
   la página del panel con @media print) y ninguna funcionó aquí, así que se
   replica el patrón que ya estaba demostrado en producción. */
export function abrirTicket(ticket: TicketData): Promise<ImpresionResultado> {
  return new Promise((resolve) => {
    const html = generarHTML(ticket, true);
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument!;
    doc.open(); doc.write(html); doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow!.focus();
        iframe.contentWindow!.print();
        resolve('ok');
      } catch {
        resolve('error');
      }
      setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 3000);
    }, 300);
  });
}

// Exporta el HTML para previsualización
export function generarTicketHTML(ticket: TicketData): string {
  return generarHTML(ticket);
}
